// routes/_authenticated/companies/$cnpj/peer-benchmark.tsx —
// peer-benchmark page for one CNPJ. Reads `year` from search params
// (default 2025) and renders the feature's main page component.
import { toDigitsCnpj } from "@shared/utils/cnpj";
import { createFileRoute, redirect } from "@tanstack/react-router";

import { PeerBenchmarkPage } from "@/features/peer-benchmark";

type ParsedSearch = { year: number };

const EMPTY_SEARCH: ParsedSearch = { year: 2025 };

function parseSearch(input: Record<string, unknown>): ParsedSearch {
  const raw = input.year;
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) && n > 1900 ? { year: n } : EMPTY_SEARCH;
}

export const Route = createFileRoute("/_authenticated/companies/$cnpj/peer-benchmark")({
  validateSearch: (input: Record<string, unknown>): ParsedSearch => parseSearch(input),
  // ponytail: same URL-canonicalization trick as the parent company route.
  // `beforeLoad` runs before any rendering; if the path param carries a
  // non-canonical CNPJ (formatted, quoted, anything with non-digits besides
  // the 14 digits), we re-emit the cleaned path with `replace: true`.
  beforeLoad: ({ params, search }) => {
    const raw = params.cnpj;
    const clean = toDigitsCnpj(raw);
    if (clean && clean !== raw) {
      throw redirect({
        to: "/companies/$cnpj/peer-benchmark",
        params: { cnpj: clean },
        search,
        replace: true,
      });
    }
  },
  component: PeerBenchmarkRoute,
});

function PeerBenchmarkRoute() {
  const { cnpj: rawCnpj } = Route.useParams();
  const search = Route.useSearch();
  const cnpj = decodeURIComponent(rawCnpj);
  return <PeerBenchmarkPage cnpj={cnpj} year={search.year} />;
}
